import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { toast } from 'sonner';

const BaseCheckoutFormSchema = z.object({
  userName: z.string().min(1, 'Required'),
  userEmail: z.string().email('Invalid email').or(z.literal('')).or(z.literal('na')),
  noCorporateEmail: z.boolean().default(false),
  address: AddressRequest.omit({ name: true }),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  customerReference: z.string().optional(),
  notes: z.string().optional()
});

const CheckoutFormSchema = BaseCheckoutFormSchema.superRefine((data, ctx) => {
  if (!data.noCorporateEmail && (!data.userEmail || data.userEmail.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required unless checked",
      path: ["userEmail"],
    });
  }
});

type CheckoutFormFields = z.infer<typeof CheckoutFormSchema>;

export interface AdvDeviceReplacementModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  checkoutPayload: {
    selectedDevice: any;
    replacementDevice: {
      isOverride: boolean;
      sku?: string;
      name: string;
      overrideText?: string;
    };
    reason: string;
    inferredServices: Array<{ rateCard: any; quantity: number; labels: string[], isInventoryItem?: boolean, name?: string, listSection?: string, originalPrice?: number, price?: number }>;
    runningTotal: number;
    devicesToBeCollected?: string[];
  } | null;
  onSuccessCallback: () => void;
}

export const AdvDeviceReplacementModal: React.FC<AdvDeviceReplacementModalProps> = ({ 
  mspId, 
  customerId, 
  checkoutPayload, 
  onSuccessCallback, 
  ...props 
}) => {
  const { partnerId, companyId } = useParams();
  const finalMspId = mspId || partnerId || '';
  const finalCustomerId = customerId || companyId || '';

  const createServiceRequest = useCreateServiceRequest();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(finalMspId, finalCustomerId);
  const [company] = useCompany(finalMspId, finalCustomerId);
  
  const initialUser = checkoutPayload?.selectedDevice?.user || '';
  
  const methods = useForm<CheckoutFormFields>({
    resolver: zodResolver(CheckoutFormSchema),
    defaultValues: {
      userName: initialUser,
      userEmail: initialUser.includes('@') ? initialUser : '',
      noCorporateEmail: false,
      deliveryContactName: '',
      deliveryContactEmail: '',
      deliveryContactMobile: '',
      customerReference: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      methods.reset({
        userName: initialUser,
        userEmail: initialUser.includes('@') ? initialUser : '',
        noCorporateEmail: false,
        deliveryContactName: '',
        deliveryContactEmail: '',
        deliveryContactMobile: '',
        customerReference: '',
        notes: ''
      });
    }
  }, [props.isOpen, initialUser, methods]);

  const noCorporateEmail = methods.watch('noCorporateEmail');

  React.useEffect(() => {
    if (noCorporateEmail) {
      methods.setValue('userEmail', 'na', { shouldValidate: true, shouldDirty: false });
    } else if (methods.getValues('userEmail') === 'na') {
      methods.setValue('userEmail', '', { shouldValidate: true, shouldDirty: false });
    }
  }, [noCorporateEmail, methods]);

  const handleAddressChange = React.useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: CheckoutFormFields) => {
    if (!checkoutPayload) return;

    const finalCustomerReference = data.customerReference?.trim() || data.userName || 'No Reference Provided';

    const logisticsDetails = {
      ...data,
      customerReference: finalCustomerReference,
      address: {
        ...data.address,
        name: 'Delivery & Collection Address'
      }
    };

    const details = {
      requestType: 'AdvDeviceReplacement',
      requestedBy: user?.email || 'Unknown',
      mspId: finalMspId,
      mspName: partner?.name || '',
      customerId: finalCustomerId,
      customerName: company?.name || '',
      raisedDateTime: new Date().toISOString(),
      customerReference: finalCustomerReference,
      submissionSource: 'DaaS Portal UI',
      totalEstimatedCost: checkoutPayload.runningTotal,
      haloClientId: company?.haloClientId || null,
      haloGeneralUserId: company?.haloGeneralUserId || null
    };

    const cart = checkoutPayload.replacementDevice.isOverride 
      ? { [`Override: ${checkoutPayload.replacementDevice.overrideText}`]: 1 }
      : { [checkoutPayload.replacementDevice.sku!]: 1 };

    const finalPayload = {
      details,
      logisticsDetails,
      collectedItems: {
        deviceToReplace: checkoutPayload.selectedDevice,
        devicesToBeCollected: checkoutPayload.devicesToBeCollected
      },
      deployedItems: { cart },
      reason: checkoutPayload.reason,
      inferredServices: checkoutPayload.inferredServices
        .filter((s: any) => !s.isInventoryItem && s.rateCard?.id)
        .map((s: any) => ({ 
          rateCardId: s.rateCard?.id, 
          name: s.name || s.rateCard?.name,
          quantity: s.quantity,
          price: s.price !== undefined ? Math.round(s.price * 100) : s.rateCard?.price?.price
        })),
      totalCost: checkoutPayload.runningTotal
    };
    
    try {
      await createServiceRequest.mutateAsync({
        params: { mspId: finalMspId, customerId: finalCustomerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'AdvDeviceReplacement',
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });
      
      toast.success('Service Request Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  if (!checkoutPayload) return null;

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Adv. Device Replacement</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the logistical details for this device replacement.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column: The Receipt */}
              <div className="space-y-6">
                <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                  <h3 className="text-lg font-bold border-b pb-3 mb-4">Replacement Summary</h3>
                  
                  <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                    <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Collect:</p>
                    <ul className="space-y-1 text-gray-600 list-disc list-inside">
                      <li>
                        {checkoutPayload.selectedDevice.type || 'Device'}: {checkoutPayload.selectedDevice.makeModel || checkoutPayload.selectedDevice.sku || 'Manual Entry'}, Serial: {checkoutPayload.selectedDevice.serial || 'none'}, Tag: {checkoutPayload.selectedDevice.assetTag || 'none'}
                      </li>
                    </ul>
                    <div className="mt-2 text-gray-600 border-t pt-2 mt-2">
                      <span className="font-semibold text-gray-800">Reason: </span>
                      {checkoutPayload.reason}
                    </div>
                  </div>

                  <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                    <p className="font-semibold text-gray-800">Replacement Device:</p>
                    <p className="text-gray-600">
                      {checkoutPayload.replacementDevice.name}
                    </p>
                    {checkoutPayload.replacementDevice.sku && (
                      <p className="text-gray-500">SKU: {checkoutPayload.replacementDevice.sku}</p>
                    )}
                  </div>

                  <div className="space-y-4 flex-1">
                    {checkoutPayload.inferredServices.filter((item: any) => item.listSection !== 'addons').map((item: any, index: number) => (
                      <div key={index} className="flex justify-between items-start text-sm">
                        <div className="pr-4">
                          <span className="font-medium">{item.quantity}x</span> {item.isInventoryItem ? (item.name || item.labels?.[0] || 'Hardware Item') : item.rateCard?.name}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {item.isInventoryItem ? (
                            <span className="italic text-gray-500">From Inv</span>
                          ) : (
                            <>
                              {item.originalPrice !== undefined && (
                                <span className="line-through text-primary-700 mr-2">
                                  {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.originalPrice)}
                                </span>
                              )}
                              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.price ?? ((item.rateCard?.price?.price || 0) / 100))}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {checkoutPayload.inferredServices.length === 0 && (
                      <div className="text-gray-500 italic text-sm">No applicable fees detected.</div>
                    )}
                    {checkoutPayload.inferredServices.some((item: any) => item.listSection === 'addons') && (
                      <>
                        <h4 className="text-sm font-semibold text-gray-500 uppercase mt-4 pt-4 border-t border-gray-200">ADD-ONS</h4>
                        {checkoutPayload.inferredServices.filter((item: any) => item.listSection === 'addons').map((item: any, index: number) => (
                          <div key={`addon-${index}`} className="flex justify-between items-start text-sm">
                            <div className="pr-4">
                              <span className="font-medium">{item.quantity}x</span> {item.name || item.rateCard?.name}
                            </div>
                            <div className="font-medium whitespace-nowrap">
                              {item.originalPrice !== undefined && (
                                <span className="line-through text-primary-700 mr-2">
                                  {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.originalPrice)}
                                </span>
                              )}
                              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.price ?? ((item.rateCard?.price?.price || 0) / 100))}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                    <span>Estimated Total</span>
                    <span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(checkoutPayload.runningTotal)}</span>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4">
                  <div>
                    <TextField id="customerReference" label="Customer Reference" placeholder="This will appear on your invoice..." optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4">
                  <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
                </div>
              </div>

              {/* Right Column: Logistics & Data Entry */}
              <div className="space-y-6">
                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">User Details</h4>
                  <TextField id="userName" label="User Name" />
                  <div className={noCorporateEmail ? 'opacity-50 pointer-events-none' : ''}>
                    <EmailField id="userEmail" label="User's Corporate Email" disabled={noCorporateEmail} />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" {...methods.register('noCorporateEmail')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    No Corporate Email
                  </label>
                </div>
                
                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery & Collection Address</h4>
                  <AddressBookSelector 
                    mspId={mspId}
                    customerId={customerId}
                    onChange={handleAddressChange}
                  />
                  {methods.formState.errors.address && (
                    <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                  )}
                </div>

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery & Collection Contact</h4>
                  <TextField id="deliveryContactName" label="Contact Name" />
                  <EmailField id="deliveryContactEmail" label="Contact Email" />
                  <TextField id="deliveryContactMobile" label="Contact Mobile" />
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={createServiceRequest.isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={createServiceRequest.isLoading}
            >
              {createServiceRequest.isLoading ? 'Submitting...' : 'Confirm Replacement & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
